<div class="kyb-key-people">
  <h2 class="mat-h2">Key People (KYB)</h2>

  <!-- Step-level errors, rendered once inside the applicant's section: under its warning, or at the
       top of its manual entry, which has no warning. Below the step when there is no applicant. -->
  <ng-template #stepErrors>
    <!-- Shown as soon as the row is entered rather than after Next, so the broker is stopped at entry. -->
    @for (error of partyIntegrityErrors; track error) {
      <message-box type="danger">{{ error }}</message-box>
    }

    <!-- Blocking conditions with no field of their own to highlight, so Next never fails silently. -->
    @if (showValidation && blockingReason) {
      <message-box type="danger">{{ blockingReason }}</message-box>
    }
  </ng-template>

  @if (primaryStatus() === 'resolved' && result) {
    @for (entity of topLevelEntities(); track entity.commercialEntityId) {
      @if (isManualEntry(entity)) {
        @if (entity.isPrimary) {
          <ng-container [ngTemplateOutlet]="stepErrors"></ng-container>
        }
        <manual-kyb-people
          [commercialEntityId]="entity.commercialEntityId"
          [initialPeople]="manualPeopleFor(entity)"
          [uploadedDocuments]="uploadedDocuments"
          [showWaitingMessage]="showManualWaitingMessage(entity)"
          [lockRoles]="isSoleTraderRoleLocked(entity)"
          (idDocumentDelete)="idDocumentDelete.emit($event)"
          (peopleChange)="onManualPeopleChange(entity.commercialEntityId, $event)"
          (validChange)="onManualValidityChange(entity.commercialEntityId, $event)">
        </manual-kyb-people>
      } @else if (statusFor(entity) === 'resolved') {
        <kyb-entity-section
          [entity]="entity"
          [entityType]="effectiveEntityType(entity)"
          [allowManualEntry]="true"
          (personRoleChange)="onPersonRoleChange($event)"
          (personSelectedChange)="onPersonSelectedChange($event)"
          (manualEntry)="startManualEntry(entity)">
        </kyb-entity-section>

        @if (kybSectionWarning(effectiveEntityType(entity)); as warning) {
          <message-box type="warn">{{ warning }}</message-box>
        }

        @if (entity.isPrimary) {
          <ng-container [ngTemplateOutlet]="stepErrors"></ng-container>
        }

        @for (error of errorsFor(entity); track error) {
          <message-box type="danger">{{ error }}</message-box>
        }

        <!-- A corporate partner/trustee is validated as its own company; its errors sit next to the
             "Select signers" table that fixes them, which is collapsed by default. Non-blocking
             warnings are raised by the page as a popup on Next instead. -->
        @for (party of corporatePartyErrorsFor(entity); track party.entity.commercialEntityId) {
          @for (error of party.errors; track error) {
            <message-box type="danger">
              {{ error }} Open "Select signers" for {{ party.entity.entityName }}.
            </message-box>
          }
        }

        <!-- A partner/trustee that is itself a trust or partnership has no officers of its own, so
             instead of a signers table it gets its own trustee/partner entry, rendered inside that
             row's own card; the parties entered there are the entities whose KYB runs supply the
             signers. Which one to render comes from the type the row's ABN/ACN lookup returned. -->
        <ng-template #nestedPartySection let-partyRow>
          @if (nestedPartyForRow(entity, partyRow); as nestedParty) {
            @if (partyControlFor(nestedParty); as nestedPartyControl) {
              <div class="kyb-nested-party">
                <div class="kyb-nested-party-heading">
                  {{ kybSectionHeading(effectiveEntityType(nestedParty), false, nestedParty.entityName) }}
                </div>
                <message-box type="warn">{{ kybSectionWarning(effectiveEntityType(nestedParty)) }}</message-box>
                @if (effectiveEntityType(nestedParty) === 'trust') {
                  <trustee
                    [formControl]="nestedPartyControl"
                    [businessNumberSearchFn]="businessNumberSearchFn"
                    [showAssetsAndLiabilities]="showAssetsAndLiabilities"
                    [kybMode]="true"
                    [allowManualEntry]="true"
                    [signerEntityFor]="signerEntityLookupFor(nestedParty)"
                    [mark]="markObservable"
                    (events)="onPartyEvent(nestedParty, 'trustee', $event)"
                    (personRoleChange)="onPersonRoleChange($event)"
                    (personSelectedChange)="onPersonSelectedChange($event)"
                    (signerResolved)="onSignerResolved($event)"
                    (signerRetry)="load(true)"
                    (manualKybEntry)="startManualEntryById($event)"
                    (manualPeopleChange)="onManualPeopleChange($event.commercialEntityId, $event.people)"
                    (manualValidityChange)="onManualValidityChange($event.commercialEntityId, $event.valid)">
                  </trustee>
                } @else {
                  <partner
                    [formControl]="nestedPartyControl"
                    [businessNumberSearchFn]="businessNumberSearchFn"
                    [showAssetsAndLiabilities]="showAssetsAndLiabilities"
                    [kybMode]="true"
                    [allowManualEntry]="true"
                    [signerEntityFor]="signerEntityLookupFor(nestedParty)"
                    [mark]="markObservable"
                    (events)="onPartyEvent(nestedParty, 'partner', $event)"
                    (personRoleChange)="onPersonRoleChange($event)"
                    (personSelectedChange)="onPersonSelectedChange($event)"
                    (signerResolved)="onSignerResolved($event)"
                    (signerRetry)="load(true)"
                    (manualKybEntry)="startManualEntryById($event)"
                    (manualPeopleChange)="onManualPeopleChange($event.commercialEntityId, $event.people)"
                    (manualValidityChange)="onManualValidityChange($event.commercialEntityId, $event.valid)">
                  </partner>
                }
              </div>
            }
          }
        </ng-template>

        @if (effectiveEntityType(entity) === 'partnership' && partyControlFor(entity); as partnerControl) {
          <div class="kyb-add-party">
            <partner
              [formControl]="partnerControl"
              [businessNumberSearchFn]="businessNumberSearchFn"
              [showAssetsAndLiabilities]="showAssetsAndLiabilities"
              [kybMode]="true"
              [allowManualEntry]="true"
              [signerEntityFor]="signerEntityLookupFor(entity)"
              [nestedPartyTemplate]="nestedPartySection"
              [mark]="markObservable"
              (events)="onPartyEvent(entity, 'partner', $event)"
              (personRoleChange)="onPersonRoleChange($event)"
              (personSelectedChange)="onPersonSelectedChange($event)"
              (signerResolved)="onSignerResolved($event)"
              (signerRetry)="load(true)"
              (manualKybEntry)="startManualEntryById($event)"
              (manualPeopleChange)="onManualPeopleChange($event.commercialEntityId, $event.people)"
              (manualValidityChange)="onManualValidityChange($event.commercialEntityId, $event.valid)">
            </partner>
          </div>
        }

        @if (effectiveEntityType(entity) === 'trust' && partyControlFor(entity); as trusteeControl) {
          <div class="kyb-add-party">
            <trustee
              [formControl]="trusteeControl"
              [businessNumberSearchFn]="businessNumberSearchFn"
              [showAssetsAndLiabilities]="showAssetsAndLiabilities"
              [kybMode]="true"
              [allowManualEntry]="true"
              [signerEntityFor]="signerEntityLookupFor(entity)"
              [nestedPartyTemplate]="nestedPartySection"
              [mark]="markObservable"
              (events)="onPartyEvent(entity, 'trustee', $event)"
              (personRoleChange)="onPersonRoleChange($event)"
              (personSelectedChange)="onPersonSelectedChange($event)"
              (signerResolved)="onSignerResolved($event)"
              (signerRetry)="load(true)"
              (manualKybEntry)="startManualEntryById($event)"
              (manualPeopleChange)="onManualPeopleChange($event.commercialEntityId, $event.people)"
              (manualValidityChange)="onManualValidityChange($event.commercialEntityId, $event.valid)">
            </trustee>
          </div>
        }
      } @else {
        <!-- This entity's own KYB run has not resolved; the rest of the step stays usable. The panel
             owns its own wait, so it polls this entity and hands back the result once it lands. -->
        <div class="kyb-entity-pending">
          <h3 class="application-subsection-title">{{ entity.entityName }}</h3>
          <kyb-status
            [applicationId]="applicationId"
            [commercialEntityId]="entity.commercialEntityId"
            [businessName]="entity.entityName"
            [pollFn]="kybResultFn"
            [refreshFn]="kybRefreshFn"
            (manualEntry)="startManualEntry(entity)"
            (resolved)="onSignerResolved($event)">
          </kyb-status>
        </div>
      }
    }

    <!-- No applicant section to hold them, so they still render rather than Next failing silently. -->
    <!-- @if (!hasPrimaryEntity()) {
      <ng-container [ngTemplateOutlet]="stepErrors"></ng-container>
    } -->
  } @else {
    <!-- The applicant's check has not produced a result yet. The step owns this wait rather than
         reporting it, because the read it made when the page loaded can predate the check finishing:
         it polls and renders the people as soon as they land. Deliberately does not send the user
         back to the company details step: that step only waits on the check for the types whose
         people come from it, so a trust or partnership would be sent somewhere that completes
         nothing. -->
    <div class="kyb-entity-pending">
      <kyb-status
        [applicationId]="applicationId"
        [active]="active"
        [businessName]="primaryEntityName"
        [pollFn]="kybResultFn"
        [refreshFn]="kybRefreshFn"
        (manualEntry)="startPrimaryManualEntry()"
        (resolved)="onSignerResolved($event)">
      </kyb-status>
    </div>
  }
</div>
